<template>
  <div>
    <a-modal v-model="visible" :confirmLoading="loading" :maskClosable="false" width="820px" @cancel="cancel" @ok="confirm">
      <div slot="title">{{ form.id ? '编辑' + kindLabel : '新增' + kindLabel }}</div>
      <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-row>
          <a-col :span="12">
            <a-form-model-item prop="name" label="名称">
              <a-input v-model="form.name" />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item prop="alias" label="别名">
              <a-input v-model="form.alias" allowClear />
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item prop="crops" label="适用作物" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
              <a-select v-model="form.crops" mode="multiple" style="width: 100%">
                <a-select-option v-for="o in cropOptions" :key="o.value" :value="o.value">{{ o.label }}</a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item prop="keywords" label="关键词" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
              <a-select v-model="form.keywords" mode="tags" style="width: 100%" placeholder="回车添加" />
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item label="图片" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
              <div class="img-hint">可上传多张，并为每张选择阶段；点「设封面」指定列表封面。</div>
              <div class="img-grid">
                <div v-for="(img, idx) in imageList" :key="img.id || idx" class="img-card" :class="{ cover: img.is_cover }">
                  <img :src="img.url" class="img-card__thumb" />
                  <a-tag v-if="img.is_cover" color="blue" class="img-card__badge">封面</a-tag>
                  <a-select
                    size="small"
                    :value="img.stage"
                    style="width: 100%; margin-top: 6px;"
                    @change="(v) => setStage(idx, v)"
                  >
                    <a-select-option v-for="s in stageOptions" :key="s.value" :value="s.value">{{ s.label }}</a-select-option>
                  </a-select>
                  <div class="img-card__actions">
                    <a @click="setCover(idx)">设封面</a>
                    <a style="color:#f5222d;" @click="removeImage(idx)">删除</a>
                  </div>
                </div>
                <a-upload :showUploadList="false" :customRequest="uploadImage" accept="image/*" class="img-upload">
                  <div class="img-upload__btn">
                    <a-icon type="plus" />
                    <div>上传</div>
                  </div>
                </a-upload>
              </div>
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item prop="description" label="形态/症状" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
              <a-textarea v-model="form.description" :rows="2" />
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item prop="harm" label="危害" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
              <a-textarea v-model="form.harm" :rows="2" />
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item prop="occurrence" label="发生规律" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
              <a-textarea v-model="form.occurrence" :rows="2" />
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item prop="control_tips" label="防治要点" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
              <a-textarea v-model="form.control_tips" :rows="2" />
            </a-form-model-item>
          </a-col>
          <a-col :span="24">
            <a-form-model-item prop="recommended_pesticides" label="推荐用药" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
              <a-select
                v-model="form.recommended_pesticides"
                mode="multiple"
                style="width: 100%"
                optionFilterProp="children"
                placeholder="从农药手册选择"
              >
                <a-select-option v-for="o in pesticideOptions" :key="o.value" :value="o.value">{{ o.label }}</a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item prop="is_active" label="启用">
              <a-switch v-model="form.is_active" />
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { plantProtectUpload } from '@/api/plantProtect'

const FALLBACK_STAGES = {
  杂草: [
    { value: 'small', label: '小草' },
    { value: 'medium', label: '中草' },
    { value: 'large', label: '大草' },
    { value: 'other', label: '其他' },
  ],
  虫害: [
    { value: 'small', label: '小龄' },
    { value: 'medium', label: '中龄' },
    { value: 'large', label: '大龄' },
    { value: 'adult', label: '成虫' },
    { value: 'other', label: '其他' },
  ],
  病害: [
    { value: 'early', label: '早期' },
    { value: 'mid', label: '中期' },
    { value: 'late', label: '后期' },
    { value: 'other', label: '其他' },
  ],
}

function uid() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)
}

function normalizeList(raw, stages) {
  const labelOf = Object.fromEntries((stages || []).map(s => [s.value, s.label]))
  const defaultStage = (stages && stages[0] && stages[0].value) || 'other'
  const list = []
  ;(raw || []).forEach(it => {
    if (typeof it === 'string') {
      if (!it) return
      list.push({ id: uid(), url: it, stage: defaultStage, is_cover: false, label: labelOf[defaultStage] || '其他' })
      return
    }
    if (it && it.url) {
      const stage = labelOf[it.stage] ? it.stage : defaultStage
      list.push({
        id: it.id || uid(),
        url: it.url,
        stage,
        is_cover: !!it.is_cover,
        label: it.label || labelOf[stage] || stage,
      })
    }
  })
  if (list.length && !list.some(i => i.is_cover)) list[0].is_cover = true
  return list
}

export default {
  name: 'HandbookFormModal',
  props: ['visible', 'form', 'cropOptions', 'pesticideOptions', 'api', 'kindLabel', 'stageOptions'],
  model: { prop: 'visible', event: 'cancel' },
  data() {
    return {
      rules: {
        name: [{ required: true, message: '请输入名称', trigger: 'change' }],
        crops: [{ required: true, type: 'array', min: 1, message: '请选择作物', trigger: 'change' }],
      },
      loading: false,
      imageList: [],
    }
  },
  computed: {
    stages() {
      if (this.stageOptions && this.stageOptions.length) return this.stageOptions
      return FALLBACK_STAGES[this.kindLabel] || FALLBACK_STAGES['杂草']
    },
  },
  watch: {
    visible(val) {
      if (val) {
        const src = (this.form && (this.form.images || this.form.image_urls)) || []
        this.imageList = normalizeList(src, this.stages)
      }
    },
  },
  methods: {
    stageLabel(stage) {
      const hit = this.stages.find(s => s.value === stage)
      return (hit && hit.label) || stage
    },
    uploadImage({ file, onSuccess, onError }) {
      const fd = new FormData()
      fd.append('file', file)
      plantProtectUpload(fd).then(data => {
        const url = data.url || data
        const emptyStage = this.stages.find(s => !this.imageList.some(i => i.stage === s.value && s.value !== 'other'))
        const stage = (emptyStage && emptyStage.value) || (this.stages[0] && this.stages[0].value) || 'other'
        this.imageList.push({
          id: uid(),
          url,
          stage,
          is_cover: this.imageList.length === 0,
          label: this.stageLabel(stage),
        })
        onSuccess && onSuccess(data)
      }).catch(err => {
        this.$message.error('上传失败')
        onError && onError(err)
      })
    },
    setStage(idx, stage) {
      const item = this.imageList[idx]
      if (!item) return
      item.stage = stage
      item.label = this.stageLabel(stage)
      this.$set(this.imageList, idx, { ...item })
    },
    setCover(idx) {
      this.imageList = this.imageList.map((it, i) => ({ ...it, is_cover: i === idx }))
    },
    removeImage(idx) {
      const wasCover = this.imageList[idx] && this.imageList[idx].is_cover
      this.imageList.splice(idx, 1)
      if (wasCover && this.imageList.length) this.imageList[0].is_cover = true
    },
    confirm() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.loading = true
        const payload = { ...this.form }
        payload.image_urls = this.imageList.map(it => ({
          id: it.id,
          url: it.url,
          stage: it.stage,
          is_cover: !!it.is_cover,
          label: it.label || this.stageLabel(it.stage),
        }))
        delete payload.cover
        delete payload.cover_url
        delete payload.images
        delete payload.recommended_pesticide_items
        delete payload.crops_display
        const func = this.form.id ? this.api.update : this.api.create
        func(payload).then(data => {
          this.$message.success(this.form.id ? '修改成功' : '新增成功')
          this.$emit(this.form.id ? 'update' : 'create', data)
          this.cancel()
        }).finally(() => {
          this.loading = false
        })
      })
    },
    cancel() {
      this.$emit('cancel', false)
      this.$refs.form && this.$refs.form.resetFields()
    },
  },
}
</script>

<style scoped>
.img-hint {
  color: #8c8c8c;
  font-size: 12px;
  margin-bottom: 8px;
}
.img-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.img-card {
  width: 110px;
  padding: 6px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
  position: relative;
}
.img-card.cover {
  border-color: #1890ff;
  box-shadow: 0 0 0 1px rgba(24, 144, 255, 0.2);
}
.img-card__thumb {
  width: 100%;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
}
.img-card__badge {
  position: absolute;
  top: 8px;
  left: 8px;
  margin: 0;
  line-height: 18px;
  font-size: 12px;
}
.img-card__actions {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 12px;
}
.img-upload__btn {
  width: 110px;
  height: 140px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c8c8c;
  cursor: pointer;
  background: #fff;
}
</style>
